import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, Navigation, Share2, Map as MapIcon, Crosshair, Clock, User, Compass } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useFleetStore } from '@/stores/fleet';
import { pointsApi, pointType } from '@/api/endpoints/points';
import { formatDateTime } from '@/lib/utils';

async function reverseNautical(lat: number, lon: number): Promise<string | null> {
  try {
    const r = await fetch(`/geo/v1/reverse?lat=${lat}&lon=${lon}&mode=nautical`, { credentials: 'include' });
    if (!r.ok) return null;
    const d = await r.json();
    return d.display_name ?? null;
  } catch { return null; }
}

export function PointDetailPage() {
  const { id } = useParams<{ id: string }>();
  const pid = Number(id);
  const positions = useFleetStore((s) => s.positions);

  const ref = useMemo(() => {
    let best: { lat: number; lon: number; t: number } | null = null;
    for (const p of positions.values()) {
      if (p.latitude == null || p.longitude == null) continue;
      const t = p.fixTime ? new Date(p.fixTime).getTime() : 0;
      if (!best || t > best.t) best = { lat: p.latitude, lon: p.longitude, t };
    }
    return best;
  }, [positions]);

  const q = useQuery({ queryKey: ['point', pid], queryFn: () => pointsApi.get(pid), enabled: Number.isFinite(pid) });
  const p = q.data;

  const dist = useQuery({
    queryKey: ['point-dist', pid, ref?.lat, ref?.lon],
    queryFn: () => pointsApi.distance(pid, ref!.lat, ref!.lon),
    enabled: !!p && !!ref,
  });

  const reverse = useQuery({
    queryKey: ['point-rev', pid],
    queryFn: () => reverseNautical(p!.latitude, p!.longitude),
    enabled: !!p,
  });

  if (q.isLoading) return <div className="p-6 text-sm text-muted-foreground">Carregando ponto…</div>;
  if (!p) return (
    <div className="space-y-4 p-4 sm:p-6">
      <BackLink />
      <p className="text-muted-foreground">Ponto não encontrado.</p>
    </div>
  );

  const t = pointType(p.type);
  const share = async () => {
    const text = `${p.name} — ${p.latitude.toFixed(5)}, ${p.longitude.toFixed(5)}\nhttps://www.google.com/maps?q=${p.latitude},${p.longitude}`;
    const nav = navigator as Navigator & { share?: (d: { title: string; text: string }) => Promise<void> };
    try { if (typeof nav.share === 'function') return await nav.share({ title: p.name, text }); await nav.clipboard.writeText(text); alert('Copiado.'); } catch { /* */ }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4 sm:p-6">
      <BackLink title={p.name} />

      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl text-2xl" style={{ background: `${t.color}22` }}>{t.emoji}</span>
        <div>
          <h1 className="text-xl font-bold">{p.name}</h1>
          <p className="text-sm text-muted-foreground">{t.label}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <a href={`/v1/navigator?dlat=${p.latitude}&dlon=${p.longitude}&dname=${encodeURIComponent(p.name)}`} className="flex flex-col items-center gap-1 rounded-xl bg-primary/10 px-2 py-3 text-xs font-semibold text-primary"><Navigation className="h-5 w-5" /> Navegar</a>
        <button type="button" onClick={share} className="flex flex-col items-center gap-1 rounded-xl bg-primary/10 px-2 py-3 text-xs font-semibold text-primary"><Share2 className="h-5 w-5" /> Compartilhar</button>
        <a href={`https://www.google.com/maps?q=${p.latitude},${p.longitude}`} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 rounded-xl bg-primary/10 px-2 py-3 text-xs font-semibold text-primary"><MapIcon className="h-5 w-5" /> Maps</a>
        <Link to="/points" className="flex flex-col items-center gap-1 rounded-xl bg-primary/5 px-2 py-3 text-xs font-semibold text-primary"><MapIcon className="h-5 w-5" /> No mapa</Link>
      </div>

      <Card>
        <CardContent className="p-0">
          <dl className="divide-y divide-border text-sm">
            <Row label="Coordenadas" value={`${p.latitude.toFixed(5)}, ${p.longitude.toFixed(5)}`} icon={Crosshair} />
            {dist.data && (
              <Row label="Distância (posição atual)" icon={Compass}
                value={`${dist.data.distance_nm.toFixed(1).replace('.', ',')} mn${dist.data.bearing ? ` a ${dist.data.bearing}` : ''} · ${dist.data.distance_km.toFixed(1).replace('.', ',')} km`} />
            )}
            {reverse.data && <Row label="Referência náutica" value={reverse.data} icon={MapIcon} />}
            {p.description && <Row label="Observação" value={p.description} />}
            <Row label="Visibilidade" value={p.visibility} />
            <Row label="Criado por" value={p.created_by ?? '—'} icon={User} />
            <Row label="Criado em" value={p.created_at ? formatDateTime(p.created_at) : '—'} icon={Clock} />
            {p.updated_at && <Row label="Atualizado em" value={formatDateTime(p.updated_at)} />}
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value, icon: Icon }: { label: string; value: string; icon?: typeof Clock }) {
  return (
    <div className="grid grid-cols-[140px_1fr] items-start gap-3 px-4 py-2.5">
      <dt className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
      </dt>
      <dd className="text-sm font-semibold">{value}</dd>
    </div>
  );
}

function BackLink({ title }: { title?: string }) {
  return (
    <div className="flex items-center justify-between">
      <Link to="/points" className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5">
        <ChevronLeft className="h-5 w-5" /> Pontos
      </Link>
      {title && <span className="truncate text-sm font-semibold text-foreground/70">{title}</span>}
      <span className="w-16" />
    </div>
  );
}
